import Service, { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import { timeout, task } from 'ember-concurrency'
import {
  buildRolloverStudentsCsvApiValidateUrl,
  buildRolloverStudentsCsvApiValidateResultUrl,
  buildRolloverStudentsCsvApiImportUrl,
} from 'teacher-ui-client/utils/import-students-csv-urls'
import { isArray } from '@ember/array'
import type { StudentImportError } from 'teacher-ui-client/student-import/utils'
import type { RolloverCandidateStudents } from 'teacher-ui-client/rollover-student-import/utils'
import {
  mapJsonApiErrorToRowError,
  normalizeValidateCsvResponse,
} from 'teacher-ui-client/rollover-student-import/utils'
import PollingStatus from 'teacher-ui-client/rollover-student-import/polling-status'
import { format } from 'date-fns'
import type { IntlService } from 'ember-intl'
import type Store from '@ember-data/store'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type SchoolRolloverService from 'teacher-ui-client/services/school-rollover'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { assert } from '@ember/debug'
import {
  JobType,
  type ValidateCsvResponse,
  type ValidateCsvSuccessResponse,
} from 'teacher-ui-client/services/student-import'
import type RolloverImportValidation from 'teacher-ui-client/models/rollover-import-validation'
import type RolloverImportSchedule from 'teacher-ui-client/models/rollover-import-schedule'

export interface ImportJob {
  file?: File // the file for this import job
  timestamp?: string // a timestamp that the BE uses for when this file was validated
  subscriptionType?: SubscriptionType
  valid?: boolean // true (file has been validated), false (file has been invalidated), undefined (file not yet validated)
  validateJobId?: string
  candidateStudents?: RolloverCandidateStudents // for a valid file, this is the list of candidate students to display on the confirm page
  validationErrors?: StudentImportError[] // for an invalid file, this is the list of errors to display on the invalid page
  imported?: boolean // true (file successfully imported), false (file failed to be imported), undefined (file not yet imported)
}

export default class RolloverStudentImportService extends Service {
  @service intl!: IntlService

  @service store!: Store

  @service authToken!: AuthToken

  @service schoolRollover!: SchoolRolloverService

  @service gradeSets!: GradeSetsService

  timeoutMilliseconds = 1000

  /**
   * { file, timestamp, valid, candidateStudents, validationErrors, imported }
   * file: the file for this importjob
   * timestamp: a timestamp that the BE uses for when this file was validated
   * valid: true (file has been validated), false (file has been invalidated), undefined (file not yet validated)
   * candidateStudents: for a valid file, this is the list of candidate students to display on the confirm page
   * validationErrors: for an invalid file, this is the list of errors to display on the invalid page
   * imported: true (file successfully imported), false (file failed to be imported), undefined (file not yet imported)
   */
  @tracked
  importJob: ImportJob = {}

  clearJob = () => {
    this.importJob = {}
  }

  // Call this to set a file for a new import job before transitioning to the validate page
  setFile(selectedFile: File) {
    // In other importer processes, it's necessary to upload the file twice (once for validation once for import) - in
    // those cases we clone the file so that it can't be edited on disk in between the uploads.
    // The gravity endpoint for rollover student import remembers the file that it validated, and instead the validation
    // is committed. So there's no need to clone the file here.
    const file = selectedFile

    const timestamp = this.getImportTimestamp()

    // New import job, from scratch, all flags reset
    this.importJob = { file, timestamp }
  }

  getImportTimestamp() {
    return format(new Date(), this.intl.t('dateFormats.dateFns.d-MMM-yy HH:mm'))
  }

  /**
   * Upload a csv file to the validate csv url, receive a job id and status in response. This job id can then be used
   * to poll the same endpoint for job completion. When complete, use the job id to query the validation result
   */
  validateCsv = task({ drop: true }, async (subscriptionType) => {
    const { file, timestamp } = this.importJob
    const rolloverId = this.schoolRollover.activeRolloverForSubco(subscriptionType)?.id

    assert('file missing for validate job', file)
    assert('timestamp missing for validate job', timestamp)
    assert('rolloverId missing for validate job', rolloverId)

    const validateUrl = buildRolloverStudentsCsvApiValidateUrl({ subscriptionType })
    const response = await this.uploadFile(file, validateUrl, rolloverId, timestamp)
    const responseBody = (await response.json()) as ValidateCsvResponse

    const jobId: string | null = responseBody?.data?.id ?? null

    // Handle a failed upload
    if (!response.ok || !jobId) {
      /* There is a special case where when the uploaded file is not UTF-8 encoded, the backend can immediately return
       * a 422 response. Ideally, the backend would always return a 200 OK response here with a jobID, and do the
       * encoding check as part of the validation job, returning this type of error AFTER polling complete, in the final
       * GET validate result step below, where all other 422 errors are handled.
       *
       * Once the encoding check in Gravity is moved from the request pipeline to the job, then this 422 check here can
       * be removed.
       * https://blakeelearning.atlassian.net/browse/YETI-410
       */
      if (response.status === 422) {
        // Should be an UTF-8 encoding issue with the file
        this.importJob = {
          ...this.importJob,
          valid: false,
          validationErrors: this.formatValidationErrors(responseBody),
        }
        return { response, responseBody }
      } else {
        // Something else went wrong, caller can decide what to do with the response
        return { response, responseBody }
      }
    }

    const pollingStatus = await this.pollServerForUploadStatus.perform(jobId, JobType.validate, subscriptionType)

    if (pollingStatus.hasFailed()) {
      return { response: { ok: false }, responseBody: { pollingStatus } }
    }

    if (pollingStatus.isComplete()) {
      // Successfully processed CSV to be either valid or invalid
      // Retreive the validation result from the result endpoint using the job id

      const headers = this.requestHeaders()
      const validateResultUrl = `${buildRolloverStudentsCsvApiValidateResultUrl({ subscriptionType })}/${jobId}`
      const resultResponse = await fetch(validateResultUrl, {
        method: 'GET',
        headers,
      })

      if (resultResponse?.ok) {
        const resultResponseBody = (await resultResponse.json()) as ValidateCsvSuccessResponse
        this.importJob = {
          ...this.importJob,
          valid: true,
          validateJobId: jobId,
          subscriptionType, // So the service knows what type the file was validated for, when we go to import it
          candidateStudents: normalizeValidateCsvResponse(resultResponseBody),
        }

        return { response: resultResponse, responseBody: resultResponseBody }
      }

      const resultResponseBody = (await resultResponse.json()) as ValidateCsvResponse
      if (resultResponse?.status === 422) {
        this.importJob = {
          ...this.importJob,
          valid: false,
          validationErrors: this.formatValidationErrors(resultResponseBody),
        }

        return { response: resultResponse, responseBody: resultResponseBody }
      }

      return { response: resultResponse, responseBody: resultResponseBody }
    }
  })

  /**
   * Use this to upload a csv file to a url with the necessary headers
   */
  async uploadFile(file: File, url: string, rolloverId: string, timestamp: string) {
    const headers = this.requestHeaders()

    const formData = new FormData()
    formData.append('file', file, file.name)
    formData.append('Content-Type', file.type)
    formData.append('timestamp', String(timestamp))
    formData.append('rollover_id', rolloverId)

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    })

    return response
  }

  /**
   * Use this to commit an import that has been validated.
   * @param {String} jobId the id of the validation job
   * @param {String} rolloverId the id of the rollover
   * @param {String} url
   * @param {String} timestamp client side creation date used in school class names
   */
  async commitImport(jobId: string, rolloverId: string, url: string, timestamp: string) {
    const headers = {
      'Content-Type': 'application/json',
      ...this.requestHeaders(),
    }

    return fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        job_id: jobId,
        rollover_id: rolloverId,
        timestamp: timestamp,
      }),
    })
  }

  formatValidationErrors(responseBody: ValidateCsvResponse): StudentImportError[] {
    const errors = responseBody?.errors
    if (isArray(errors)) {
      // Provide a list of grade names, that can be used in error messages about grade position
      const allowedGradeValues = this.gradeSets.gradeNames

      return errors.map((error) => {
        return mapJsonApiErrorToRowError(error, responseBody, this.intl, allowedGradeValues)
      })
    }
    return []
  }

  /**
   * A task to commit to importing a pre-validated csv. An asynchronous job is polled to
   * retrieve the status of processing. On error we will show a flash message. On success we will redirect to
   * the manage.rollover.steps.students page. Because the task is running on the service we can
   * invoke the redirect from any location.
   *
   * @type {Task}
   * @returns {PollingStatus}
   */
  importCsv = task({ drop: true }, async () => {
    // get the needed arguments from the import job & ensuring it was validated
    const { valid, validateJobId, subscriptionType, timestamp } = this.importJob

    assert('subscriptionType is missing for import job', subscriptionType)
    const rolloverId = this.schoolRollover.activeRolloverForSubco(subscriptionType)?.id

    assert('validateJobId is missing for import job', validateJobId)
    assert('rolloverId is missing for import job', rolloverId)
    assert('timestamp is missing for import job', timestamp)

    if (!valid) return { response: { ok: false }, responseBody: {} }

    const importUrl = buildRolloverStudentsCsvApiImportUrl({ subscriptionType })
    const response = await this.commitImport(validateJobId, rolloverId, importUrl, timestamp)
    const responseBody = await response.json()

    const jobId = responseBody?.data?.id ?? null

    if (!response.ok || !jobId) return { response, responseBody }

    const pollingStatus = await this.pollServerForUploadStatus.perform(jobId, JobType.import, subscriptionType)

    if (pollingStatus.hasFailed()) {
      this.importJob = { ...this.importJob, imported: false }
      return { response: { ok: false }, responseBody: { pollingStatus } }
    }

    if (pollingStatus.isComplete()) {
      this.importJob = { ...this.importJob, imported: true }
      return { response: { ok: true }, responseBody: { pollingStatus } }
    }
  })

  /**
   * Creates a task which polls gravity infinitely to ask for a result response and will
   * continue until the status is either completed or error. We will pause for x seconds after
   * the request finished and start again until we are done.
   *
   * @param {String} jobId
   * @param {String} jobType - either 'validate' or 'import', indicates what endpoint to poll
   * @returns {PollingStatus} // returns a polling status object with all information about the polling request
   */
  pollServerForUploadStatus = task(
    { drop: true },
    async (jobId: string, jobType: JobType, subscriptionType: SubscriptionType) => {
      await timeout(this.timeoutMilliseconds) // wait x milliseconds before we start the polling
      // fetch response
      // get json data of the response
      // convert into PollingStatus object
      // [TJ] note: I keep this separate (even though it is duplicated), due to it's async behaviour within the task
      let jobModel = await this.callGetProgress.perform(jobId, jobType, subscriptionType)
      let modelStatus = jobModel?.status || null
      let status = this.getPollingStatusFromResponse(modelStatus, jobId)

      // continue polling as long as the status is not `complete`, `error` or null
      while (status.isStillRunning()) {
        await timeout(this.timeoutMilliseconds * 4) // wait x * 4 milliseconds after the last request finished
        jobModel = await this.callGetProgress.perform(jobId, jobType, subscriptionType)
        modelStatus = jobModel?.status || null
        status = this.getPollingStatusFromResponse(modelStatus, jobId)
      }

      return status
    },
  )

  /**
   * Calls endpoint to retrieve an status object
   */
  private callGetProgress = task(async (jobId: string, jobType: JobType, subscriptionType: SubscriptionType) => {
    const response =
      jobType === JobType.validate
        ? await this.store.findRecord<RolloverImportValidation>('rollover-import-validation', jobId, {
            reload: true,
            adapterOptions: { subscriptionType },
          })
        : await this.store.findRecord<RolloverImportSchedule>('rollover-import-schedule', jobId, {
            reload: true,
            adapterOptions: { subscriptionType },
          })
    return response
  })

  /**
   * If we cant get a status value this will always return null
   * and indicate that a response failed
   *
   * @param {RolloverImportScheduleModel} record
   * @param jobId
   * @returns {PollingStatus}
   * @private
   */
  private getPollingStatusFromResponse(status: string | null, jobId: string) {
    return new PollingStatus(status, jobId)
  }

  private requestHeaders() {
    const headers: Record<string, string> = {
      Accept: ['application/vnd.api+json', 'json-api'].join(','),
    }
    if (this.authToken.token) {
      headers.Authorization = this.authToken.token
    }
    return headers
  }
}

declare module '@ember/service' {
  interface Registry {
    'rollover-student-import': RolloverStudentImportService
  }
}
